import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../utils/translations';
import { useTheme } from '../../context/ThemeContext'; // Added for dark mode
import supabase from '../../lib/supabase';
import Button from "../Common/Button"
import { toast } from "sonner"

const { FiChevronDown, FiUser, FiLogOut, FiSun, FiMoon, FiLock, FiEye, FiEyeOff } = FiIcons;

const Topbar = ({ title, subtitle, setActiveTab, activeTab, setActiveTeam: setActiveTeamCallback }) => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme(); // Added theme context
  const t = useTranslation(user?.language || 'fr'); // Default to French
  const [teams, setTeams] = useState([]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changeForm, setChangeForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    const fetchUserTeams = async () => {
      if (!user) return;

      try {
        const { data: teamMemberships, error: membershipError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('team_id')
          .eq('user_id', user.id);

        if (membershipError) throw membershipError;

        if (teamMemberships && teamMemberships.length > 0) {
          const teamIds = teamMemberships.map(tm => tm.team_id);

          const { data: userTeams, error: teamsError } = await supabase
            .from('teams_a1b2c3d4e5')
            .select('*')
            .in('id', teamIds);

          if (teamsError) throw teamsError;

          setTeams(userTeams || []);
        }
      } catch (error) {
        console.error('Error fetching user teams:', error);
      }
    };

    fetchUserTeams();
  }, [user, setActiveTeamCallback]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  const handleNavigation = (tab) => {
    if (setActiveTab && typeof setActiveTab === 'function') {
      setActiveTab(tab);
    }
    setIsProfileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsProfileMenuOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setChangeLoading(true);
    setChangeError('');

    const { newPassword, confirmPassword } = changeForm;
    
    try {
      if (newPassword !== confirmPassword) {
        throw new Error(t('auth.passwordsDoNotMatch', 'Les mots de passe ne correspondent pas'));
      }

      if (newPassword.length < 8 || newPassword.length > 15) {
        throw new Error(t('auth.invalidPasswordLength', 'Le mot de passe doit contenir entre 8 et 15 caractères.'));
      }

      const passwordRegex = new RegExp(`^(?=.*[A-Za-z])(?=.*\\d)(?=.*[^A-Za-z\\d]).{${8},${15}}$`)
      if (!passwordRegex.test(newPassword)) {
        throw new Error(t('auth.invalidPasswordFormat', 'Le mot de passe doit être alphanumérique et contenir au moins un caractère spécial.'));
      }

      // Update password with Supabase
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      toast.success(t('auth.passwordUpdated', 'Votre mot de passe a été mis à jour avec succès.'));      

      setChangeForm({ newPassword: '', confirmPassword: '' });
      setShowChangePasswordModal(false);
      handleLogout();
    } catch (err) {
      setChangeError(err.message);
    } finally {
      setChangeLoading(false);
    }
  };

  const handleChangeForm = (e) => {
    setChangeForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleOpenChangePassword = () => {
    setShowChangePasswordModal(true);
    setIsProfileMenuOpen(false);
    setChangeError('');
    setChangeForm({ newPassword: '', confirmPassword: '' });
  };

  const handleCloseChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setChangeError('');
    setChangeForm({ newPassword: '', confirmPassword: '' });
  };

  return (
    <>
      <div className="h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center space-x-2">
          {title && (
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Dark Mode Toggle */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={toggleTheme}
            className="flex items-center px-3 py-1.5 rounded-lg font-medium transition-colors text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200"
            title={isDark ? t('topBar.lightMode', 'Light Mode') : t('topBar.darkMode', 'Dark Mode')}
          >
            <SafeIcon icon={isDark ? FiSun : FiMoon} className="w-5 h-5" />
          </motion.button>

          <div className="relative" ref={menuRef}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 cursor-pointer"
            >
              <img
                src={user?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullName || 'User')}&background=007ee5&color=fff`}
                alt={t('topBar.userAvatarAlt', 'User')}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullName || 'User')}&background=007ee5&color=fff`;
                }}
              />
            </motion.div>

            {isProfileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50"
                style={{ minWidth: '250px' }}
              >
                <motion.button
                  whileHover={{ backgroundColor: '#f3f4f6' }}
                  onClick={() => handleNavigation('profile')}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-400 transition-colors"
                >
                  <SafeIcon icon={FiUser} className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                  {t('topBar.profile', 'Profile')}
                </motion.button>
                <motion.button
                  whileHover={{ backgroundColor: '#f3f4f6' }}
                  onClick={handleOpenChangePassword}
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-400 transition-colors"
                >
                  <SafeIcon icon={FiLock} className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                  {t('auth.changePassword', 'Modifier mon mot de passe')}
                </motion.button>
                <hr className="border-t border-gray-200 dark:border-gray-700 my-1" /> {/* Separator */}
                <motion.button
                  whileHover={{ backgroundColor: '#fef2f2' }}
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-2 text-red-500 dark:text-red-400" />
                  {t('topBar.logout', 'Logout')}
                </motion.button>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseChangePasswordModal}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {t('auth.changePassword', 'Modifier mon mot de passe')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('auth.updateDescription', 'Entrez votre nouveau mot de passe. Il doit contenir 8 à 15 caractères alphanumériques avec au moins un caractère spécial.')}
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleChangePasswordSubmit}>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('auth.newPassword', 'Nouveau mot de passe')}
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    value={changeForm.newPassword}
                    onChange={handleChangeForm}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <SafeIcon icon={FiLock} className="absolute left-3 top-2.5 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <Button
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-2.5"
                    icon={showNewPassword ? FiEyeOff : FiEye}
                    iconClassName="w-5 h-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('auth.confirmPassword', 'Confirmer le nouveau mot de passe')}
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={changeForm.confirmPassword}
                    onChange={handleChangeForm}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <SafeIcon icon={FiLock} className="absolute left-3 top-2.5 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <Button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5"
                    icon={showConfirmPassword ? FiEyeOff : FiEye}
                    iconClassName="w-5 h-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                  />
                </div>
              </div>

              {changeError && (
                <div className="text-red-600 dark:text-red-400 text-sm text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {changeError}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  onClick={handleCloseChangePasswordModal}
                  disabled={changeLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                >
                  {t('auth.cancel', 'Annuler')}
                </Button>
                <Button
                  type="submit"
                  disabled={changeLoading}
                  loading={changeLoading}
                  appendClassName="px-4 py-2"
                >
                  {t('auth.updatePassword', 'Mettre à jour le mot de passe')}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Topbar;